"use client";

import React from "react";
import Link from "next/link";
import { useIsLg } from "@/hooks/useIsLg";
import { ModeToggle } from "./theme-btn";
import {
  aboutPage,
  marketPlacePage,
  notificationPage,
  profilePage,
  settingsPage,
  homePage,
  explorePage,
  clipsPage,
  chatsPage,
  electionCandidatesPage,
  adminPage,
} from "@/constants/page";
import Image from "next/image";
import { useRouter, usePathname } from "next/navigation";
import { useAuth } from "@/contexts/authContext";
import { NavItem } from "@/types";
import LogoutButton from "./ui/logOutButton";
import { useSelector } from "react-redux";
import { RootState } from "@/lib/features/store";
import TrendingTagsClient from "./explore/TrendingTagsClient";
import AdComponent from "./AdSense";

const Sidebar = () => {
  const router = useRouter();
  const { user, logout, isAuthenticated } = useAuth();
  // const adminUsersid = ["0x1", "0x4", "0xc"];
  // const isAdmin = adminUsersid.includes(user?.id || "");

  // Create nav items array inside component to avoid duplicates
  const typedNavItems: NavItem[] = [
    homePage,
    chatsPage,
    notificationPage,
    explorePage,
    clipsPage,
    profilePage,
    marketPlacePage,
    electionCandidatesPage,
    settingsPage,
    // aboutPage,
    ...(user?.isAdmin ? [adminPage] : []),
  ];

  const pathname = usePathname();
  const isLg = useIsLg();
  const unreadCount = useSelector(
    (state: RootState) => state.notification.count
  );
  const chatCount = useSelector(
    (state: RootState) => state.notification.chatCount
  );

  const normalizePath = (path: string) => {
    const normalized = path.replace(/\/+$/, "");
    return normalized === "" ? "/" : normalized;
  };

  const currentPath = pathname ? normalizePath(pathname) : "";

  // Only hide on public elections page (e.g. "/elections"),
  // keep it visible on admin routes like "/admin/elections/..."
  if (currentPath.startsWith("/elections")) {
    return null;
  }
  if (!isAuthenticated) {
    return (
      <div className="backdrop-blur-3xl hidden md:hidden lg:flex flex-col">
        <aside className="hidden md:block h-screen lg:w-[20vw] md:w-[10vw] border-r border-white/75 dark:border-white/25 bg-white/25 dark:bg-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] text-[#020817] dark:text-[#F8FAFC] sticky top-0">
          <div className="h-fit flex flex-col">
            <div className="flex-1 overflow-y-auto px-2 pt-6 mb-6">
              <TrendingTagsClient />
            </div>
            <AdComponent adType="feed" id={`left-sidebar-ad`} />
          </div>
        </aside>
      </div>
    );
  }

  return (
    <div className="backdrop-blur-3xl">
      <aside className="hidden md:block h-screen rounded-r-2xl lg:w-[20vw] md:w-[10vw] border-r border-white/75 dark:border-white/25 bg-white/25 dark:bg-white/10 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] text-[#020817] dark:text-[#F8FAFC] sticky top-0">
        <div className="h-full flex flex-col">
          {/* Header */}
          <div className="p-4 flex items-center justify-between">
            <Link
              href={homePage.to}
              className="text-xl font-bold hover:scale-105 transition-all duration-300"
              data-cy="sidebar-link-home"
            >
              <div className="w-10 h-10 flex items-center justify-center rounded-2xl bg-white/25 hover:bg-white/30 dark:bg-white/10 dark:hover:bg-white/20 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] border border-white/75 dark:border-white/25 transition-all duration-300">
                <div className="flex items-center justify-center rounded-full">
                  <Image
                    src="/public/Logo_SocialGraphs.png"
                    alt="SocialGraphs Logo"
                    width={50}
                    height={50}
                    className="rounded-full object-cover"
                  />
                </div>
              </div>
            </Link>

            <div className="hidden lg:block" data-cy="sidebar-theme-toggle">
              <ModeToggle />
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 lg:overflow-y-auto px-2">
            <ul className="space-y-2">
              {typedNavItems.map((item) => {
                const isProfile = item.name === "Profile";
                const baseHref =
                  isProfile && user?.name
                    ? `/profile/${user.name}`
                    : item.toLg && isLg
                      ? item.toLg
                      : item.to;
                const href = baseHref;
                const itemPath = normalizePath(href);
                const isActive =
                  itemPath === "/"
                    ? currentPath === "/"
                    : currentPath === itemPath ||
                      currentPath.startsWith(`${itemPath}/`);

                const dataCyName = `sidebar-link-${item.name}`;

                return (
                  <li key={item.to}>
                    <Link
                      href={href}
                      className={`flex items-center gap-3 px-4 py-3 rounded-2xl transition-all duration-300 border border-white/75 dark:border-white/25 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] hover:scale-[1.02] ${
                        isActive
                          ? "bg-gray-900 text-white dark:bg-gray-300 dark:text-black font-semibold"
                          : "bg-white/25 dark:bg-white/10 hover:bg-white/30 dark:hover:bg-white/20"
                      }`}
                      data-cy={dataCyName}
                    >
                      <div className="relative">
                        {item.icon && <item.icon className="h-6 w-6" />}
                        {item.to === notificationPage.to && unreadCount > 0 && (
                          <span className="absolute -top-3 -right-2 bg-gradient-to-r from-[#e0aa3e]/90 to-[#f9f295]/90 text-black text-[12px]  min-w-fit rounded-full h-6 w-6 flex items-center justify-center shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] border border-white/75">
                            {unreadCount > 99 ? "99+" : unreadCount}
                          </span>
                        )}
                        {item.to === chatsPage.to && chatCount > 0 && (
                          <span className="absolute -top-2 -right-2 bg-gradient-to-r from-[#e0aa3e]/90 to-[#f9f295]/90 text-black text-xs rounded-full h-5 w-5 flex items-center justify-center shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] border border-white/75">
                            {chatCount}
                          </span>
                        )}
                      </div>
                      <span
                        className="font-medium md:hidden lg:block truncate max-w-[120px] overflow-hidden whitespace-nowrap"
                        title={item.name}
                      >
                        {item.name}
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </nav>

          {/* Footer */}
          <div className="hidden md:flex flex-col gap-4 p-4 border-t border-white/75 dark:border-white/25 mt-auto">
            {/* Tablet-only theme toggle (placed above Logout) */}
            <div
              className="lg:hidden flex h-14 items-center justify-center bg-white/25 dark:bg-white/10 rounded-full border border-white/75 dark:border-white/25 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] p-2"
              data-cy="sidebar-theme-toggle-tablet"
            >
              <ModeToggle />
            </div>
            <div
              className="bg-white/25 dark:bg-white/10 rounded-full border border-white/75 dark:border-white/25 shadow-[0_4px_30px_rgba(0,0,0,0.1)] backdrop-blur-[1.9px] p-2"
              data-cy="sidebar-logout-container"
            >
              <LogoutButton />
            </div>
          </div>
        </div>
      </aside>
    </div>
  );
};

export default Sidebar;
